import { useEffect, useState } from "react";
import { searchEventsWithAI } from "../services/apiAI.js";
import {
    getCities,
    getCountries,
    getCountryCode,
    getRegions,
} from "../constants/geography.js";
import { createEditEvent, getEvents } from "../services/apiEvents.js";
import { useQueryClient } from "@tanstack/react-query";

const EDITABLE_FIELDS = [
    { key: "name", label: "Event name", required: true, wide: true },
    { key: "country", label: "Country", required: true },
    { key: "state", label: "State / region", required: true },
    { key: "city", label: "City", required: true },
    { key: "venue", label: "Venue", required: true },
    { key: "startDatetime", label: "Starts", required: true, type: "datetime-local" },
    { key: "type", label: "Type" },
    { key: "ticketsUrl", label: "Tickets URL", type: "url", required: true, wide: true },
    { key: "description", label: "Description", textarea: true, required: true, wide: true },
];

const normalizeText = (value) =>
    String(value || "").trim().toLocaleLowerCase();

const normalizeEvent = (event, defaults = {}) => ({
    name: event.name ?? event.Name ?? "",
    country: event.country ?? event.Country ?? defaults.country ?? "",
    state: event.state ?? event.State ?? defaults.state ?? "",
    city: event.city ?? event.City ?? defaults.city ?? "",
    venue: event.venue ?? event.Venue ?? "",
    startDatetime: event.startDatetime ?? event.StartDateTime ?? "",
    type: event.type ?? event.Type ?? "Party",
    ticketsUrl:
        event.ticketsUrl ??
        event.TicketsUrl ??
        event.ticketUrl ??
        event.TicketUrl ??
        event.eventUrl ??
        event.EventUrl ??
        "",
    description: event.description ?? event.Description ?? "",
});

const isSameTimeAndPlace = (candidate, storedEvent) => {
    const candidateTime = new Date(candidate.startDatetime).getTime();
    const storedTime = new Date(storedEvent.startDatetime).getTime();

    return (
        Number.isFinite(candidateTime) &&
        candidateTime === storedTime &&
        normalizeText(candidate.city) === normalizeText(storedEvent.city) &&
        normalizeText(candidate.venue) === normalizeText(storedEvent.venue)
    );
};

const toDatetimeLocal = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return value;

    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const getSafeExternalUrl = (value) => {
    try {
        const url = new URL(String(value || "").trim());
        return ["http:", "https:"].includes(url.protocol) ? url.href : "";
    } catch {
        return "";
    }
};

const DEFAULT_PROMPT = `Find verified upcoming queer dance parties and nightlife events for the selected location during the next 90 days.

Return only a valid JSON array.

Each event must contain:
- name
- country
- state
- city
- venue
- ticketsUrl
- description
- type
- startDatetime

Rules:
- Use ISO 8601 for startDatetime.
- City must be present.
- Use null only when an optional value cannot be verified.
- Do not invent events, venues, dates, or URLs.
- Only include future events supported by credible sources.
- Prefer official promoter, venue, event, or ticketing pages.
- ticketsUrl must be the direct, verified event or ticket page used to research that event, not a homepage, search page, or social profile.
- Exclude an event when a direct ticketsUrl cannot be verified.
- Read the ticketsUrl page and use its event details to write an original description of 1–3 sentences.
- Make the description fun, cheeky, welcoming, and useful, while staying factual. Paraphrase the source; never copy its promotional text verbatim.
- Do not add details to the description that are not supported by the source page.
- Do not wrap the JSON in Markdown code fences.`;

function SmartEventGen() {
    const queryClient = useQueryClient();
    const [country, setCountry] = useState("Australia");
    const [stateRegion, setStateRegion] = useState("Victoria");
    const [city, setCity] = useState("Melbourne");
    const [prompt, setPrompt] = useState(DEFAULT_PROMPT);

    const [loading, setLoading] = useState(false);
    const [searchProgress, setSearchProgress] = useState("");
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    useEffect(() => {
        if (!loading) return;
        const started = Date.now();
        const timer = setInterval(() => setElapsedSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [loading]);
    const [parsedEvents, setParsedEvents] = useState([]);
    const [existingEvents, setExistingEvents] = useState([]);
    const [savingIndex, setSavingIndex] = useState(null);
    const [savedIndexes, setSavedIndexes] = useState(new Set());
    const [citations, setCitations] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");

    const countries = getCountries();
    const regions = getRegions(country);
    const cities = getCities(country, stateRegion);
    const countryCode = getCountryCode(country);

    const canGenerate =
        !loading &&
        country.trim().length > 0 &&
        city.trim().length > 0 &&
        stateRegion.trim().length > 0;

    const handleCountryChange = (event) => {
        const nextCountry = event.target.value;
        const nextRegions = getRegions(nextCountry);
        const nextState = nextRegions[0]?.name || "";
        const nextCities = getCities(nextCountry, nextState);

        setCountry(nextCountry);
        setStateRegion(nextState);
        setCity(nextCities[0] || "");
    };

    const handleStateChange = (event) => {
        const nextState = event.target.value;
        const nextCities = getCities(country, nextState);

        setStateRegion(nextState);
        setCity(nextCities[0] || "");
    };

    const handleFetch = async () => {
        if (!country.trim()) {
            setErrorMessage("Please select a country.");
            return;
        }

        if (!stateRegion.trim()) {
            setErrorMessage("Please select a state or region.");
            return;
        }

        if (!city.trim()) {
            setErrorMessage("Please enter a city.");
            return;
        }

        try {
            setElapsedSeconds(0);
            setSearchProgress("Starting search�");
            setLoading(true);
            setErrorMessage("");
            setParsedEvents([]);
            setExistingEvents([]);
            setSavedIndexes(new Set());
            setCitations([]);

            const [result, storedEvents] = await Promise.all([
                searchEventsWithAI({
                    country,
                    countryCode,
                    state: stateRegion,
                    city: city.trim(),
                    prompt: prompt.trim() || undefined,
                    onProgress: setSearchProgress,
                }),
                getEvents(),
            ]);

            setParsedEvents(
                result.events.map((event) =>
                    normalizeEvent(event, {
                        country,
                        state: stateRegion,
                        city,
                    })
                )
            );
            setExistingEvents(storedEvents || []);
            setCitations(result.citations);
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : String(error);

            setErrorMessage(message);
        } finally {
            setLoading(false);
        }
    };

    const handleEventChange = (index, field, value) => {
        setParsedEvents((events) =>
            events.map((event, eventIndex) =>
                eventIndex === index
                    ? { ...event, [field]: value }
                    : event
            )
        );
        setSavedIndexes((indexes) => {
            const next = new Set(indexes);
            next.delete(index);
            return next;
        });
    };

    const handleSaveEvent = async (index) => {
        const event = parsedEvents[index];
        const requiredFields = [
            "name",
            "country",
            "state",
            "city",
            "venue",
            "startDatetime",
            "ticketsUrl",
            "description",
        ];
        const missingField = requiredFields.find((field) => !String(event[field] || "").trim());

        if (missingField) {
            setErrorMessage(`Complete the ${missingField} field before saving.`);
            return;
        }

        if (!getSafeExternalUrl(event.ticketsUrl)) {
            setErrorMessage("Enter a valid http or https tickets URL before saving.");
            return;
        }

        try {
            setSavingIndex(index);
            setErrorMessage("");
            await createEditEvent({ ...event });
            setExistingEvents((events) => [...events, { ...event }]);
            setSavedIndexes((indexes) => new Set(indexes).add(index));
            await queryClient.invalidateQueries({ queryKey: ["events"] });
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "Could not save the event."
            );
        } finally {
            setSavingIndex(null);
        }
    };

    const handleResetPrompt = () => {
        setPrompt(DEFAULT_PROMPT);
    };

    return (
        <div className="admin-tool space-y-5">
            <div>
                <h2>Find events with AI</h2>

                <p className="mt-1 text-sm text-gray-600">
                    Search for upcoming events, review the results.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium">
                        Country
                    </span>

                    <select
                        value={country}
                        onChange={handleCountryChange}
                        disabled={loading}
                        className="rounded-md border px-3 py-2"
                    >
                        {countries.map((item) => (
                            <option
                                key={item}
                                value={item}
                            >
                                {item}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium">
                        State / Region
                    </span>

                    <select
                        value={stateRegion}
                        onChange={handleStateChange}
                        disabled={loading || regions.length === 0}
                        className="rounded-md border px-3 py-2"
                    >
                        {regions.map((item) => (
                            <option key={item.code} value={item.name}>
                                {item.code} — {item.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium">
                        City
                    </span>

                    <select
                        value={city}
                        onChange={(event) =>
                            setCity(event.target.value)
                        }
                        disabled={loading || cities.length === 0}
                        className="rounded-md border px-3 py-2"
                    >
                        {cities.map((item) => (
                            <option key={item} value={item}>
                                {item}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <label className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium">
                        Search instructions
                    </span>

                    <button
                        type="button"
                        onClick={handleResetPrompt}
                        disabled={loading}
                        className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                    >
                        Reset prompt
                    </button>
                </div>

                <textarea
                    value={prompt}
                    onChange={(event) =>
                        setPrompt(event.target.value)
                    }
                    disabled={loading}
                    rows={9}
                    className="rounded-md border px-3 py-2 font-mono text-sm"
                    placeholder="Describe which events should be found…"
                />
            </label>

            {errorMessage && (
                <div
                    role="alert"
                    className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                >
                    <strong>Something went wrong:</strong>{" "}
                    {errorMessage}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={handleFetch}
                    disabled={!canGenerate}
                    className="admin-primary-button disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {loading
                        ? "Searching for events…"
                        : "Find events"}
                </button>
            </div>

            {loading && (
                <div className="rounded-lg border border-pink-200 bg-pink-50 p-3 text-sm text-purple-900">
                    <p role="status">{searchProgress}</p>
                    <p className="mt-1 text-xs text-gray-600">
                        {elapsedSeconds}s elapsed. Detailed web research can take a few minutes.
                    </p>
                </div>
            )}

            {parsedEvents.length > 0 && (
                <section className="ai-results">
                    <header className="ai-results-header">
                        <div>
                            <h2>{parsedEvents.length} events found</h2>
                            <p>Review and edit each event before adding it to the database.</p>
                        </div>
                    </header>

                    <div className="ai-event-list">
                        {parsedEvents.map((event, index) => {
                            const duplicate = existingEvents.some((storedEvent) =>
                                isSameTimeAndPlace(event, storedEvent)
                            );
                            const saved = savedIndexes.has(index);

                            return (
                                <article key={index} className="ai-event-card">
                                    <header className="ai-event-card-header">
                                        <div>
                                            <p className="ai-event-number">Event {index + 1}</p>
                                            <h3>{event.name || "Unnamed event"}</h3>
                                        </div>
                                        <span
                                            className={`ai-event-status ${
                                                duplicate ? "is-duplicate" : "is-new"
                                            }`}
                                        >
                                            {saved
                                                ? "Added"
                                                : duplicate
                                                    ? "Already in database"
                                                    : "New"}
                                        </span>
                                    </header>

                                    <div className="ai-event-fields">
                                        {EDITABLE_FIELDS.map((field) => (
                                            <label
                                                key={field.key}
                                                className={field.wide ? "is-wide" : ""}
                                            >
                                                <span>
                                                    {field.label}
                                                    {field.required ? " *" : ""}
                                                </span>
                                                {field.textarea ? (
                                                    <textarea
                                                        rows={3}
                                                        value={event[field.key] || ""}
                                                        onChange={(changeEvent) =>
                                                            handleEventChange(
                                                                index,
                                                                field.key,
                                                                changeEvent.target.value
                                                            )
                                                        }
                                                    />
                                                ) : field.key === "ticketsUrl" ? (
                                                    <div className="ai-url-field">
                                                        <input
                                                            type="url"
                                                            value={event.ticketsUrl || ""}
                                                            onChange={(changeEvent) =>
                                                                handleEventChange(
                                                                    index,
                                                                    field.key,
                                                                    changeEvent.target.value
                                                                )
                                                            }
                                                        />
                                                        <button
                                                            type="button"
                                                            className="admin-secondary-button"
                                                            disabled={!getSafeExternalUrl(event.ticketsUrl)}
                                                            onClick={() =>
                                                                window.open(
                                                                    getSafeExternalUrl(event.ticketsUrl),
                                                                    "_blank",
                                                                    "noopener,noreferrer"
                                                                )
                                                            }
                                                            aria-label={`Open ticket page for ${event.name || "event"}`}
                                                        >
                                                            Open
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <input
                                                        type={field.type || "text"}
                                                        value={
                                                            field.type === "datetime-local"
                                                                ? toDatetimeLocal(event[field.key])
                                                                : event[field.key] || ""
                                                        }
                                                        onChange={(changeEvent) =>
                                                            handleEventChange(
                                                                index,
                                                                field.key,
                                                                changeEvent.target.value
                                                            )
                                                        }
                                                    />
                                                )}
                                            </label>
                                        ))}
                                    </div>

                                    <footer className="ai-event-card-footer">
                                        <p>
                                            Duplicate matching uses the exact start time,
                                            city, and venue.
                                        </p>
                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={() => handleSaveEvent(index)}
                                            disabled={
                                                duplicate ||
                                                saved ||
                                                savingIndex !== null
                                            }
                                        >
                                            {savingIndex === index
                                                ? "Adding…"
                                                : saved
                                                    ? "Added"
                                                    : duplicate
                                                        ? "Already added"
                                                        : "Add to database"}
                                        </button>
                                    </footer>
                                </article>
                            );
                        })}
                    </div>
                </section>
            )}

            {citations.length > 0 && (
                <section className="rounded-lg border border-blue-300 bg-blue-50 p-4">
                    <h2 className="mb-3 text-lg font-semibold">
                        Sources
                    </h2>

                    <ul className="space-y-2">
                        {citations.map(
                            (citation, index) => {
                                const citationData =
                                    citation.url_citation ||
                                    citation;

                                const url =
                                    citationData.url;

                                const title =
                                    citationData.title ||
                                    url ||
                                    `Source ${index + 1}`;

                                if (!url) {
                                    return null;
                                }

                                return (
                                    <li
                                        key={`${url}-${index}`}
                                        className="text-sm"
                                    >
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-700 hover:underline"
                                        >
                                            {title}
                                        </a>
                                    </li>
                                );
                            }
                        )}
                    </ul>
                </section>
            )}
        </div>
    );
}

export default SmartEventGen;
